/**
 * useRoles.js — Week 4
 *
 * Central role system for NexusFlow.
 *
 * Role hierarchy (lowest → highest):
 *   viewer   → read-only: can view campaigns, decisions, dashboard
 *   manager  → viewer + can create/evaluate campaigns
 *   admin    → manager + can manage team, view admin dashboard, change features
 *   superadmin → full system access (NexusFlow staff only)
 *
 * Stored in Firestore users/{uid}.role
 * Legacy values: "owner" maps to admin, "member" maps to viewer
 */

import { useAuth } from "../contexts/AuthContext";

/* ── Role constants ──────────────────────────────────────── */
export const ROLES = {
  VIEWER:     "viewer",
  MANAGER:    "manager",
  ADMIN:      "admin",
  SUPERADMIN: "superadmin",
};

/* Map legacy Firestore values to canonical roles */
const LEGACY_MAP = {
  owner:        ROLES.ADMIN,
  admin:        ROLES.ADMIN,
  manager:      ROLES.MANAGER,
  member:       ROLES.VIEWER,
  viewer:       ROLES.VIEWER,
  superadmin:   ROLES.SUPERADMIN,
  system_admin: ROLES.SUPERADMIN,
};

/* Numeric rank for comparison */
const RANK = {
  [ROLES.VIEWER]:     1,
  [ROLES.MANAGER]:    2,
  [ROLES.ADMIN]:      3,
  [ROLES.SUPERADMIN]: 4,
};

/* Canonical role from any stored value */
export function normaliseRole(raw) {
  if (!raw) return ROLES.VIEWER;
  return LEGACY_MAP[raw.toLowerCase()] || ROLES.VIEWER;
}

/* ── Visual config ───────────────────────────────────────── */
export const ROLE_META = {
  [ROLES.VIEWER]:     { labelKey: "role_viewer",     cls: "bg-gray-500/20    text-gray-400    border-gray-500/40"    },
  [ROLES.MANAGER]:    { labelKey: "role_manager",    cls: "bg-blue-500/20    text-blue-400    border-blue-500/40"    },
  [ROLES.ADMIN]:      { labelKey: "role_admin",      cls: "bg-purple-500/20  text-purple-400  border-purple-500/40"  },
  [ROLES.SUPERADMIN]: { labelKey: "role_superadmin", cls: "bg-red-500/20     text-red-400     border-red-500/40"     },
};

/* ── Hook ────────────────────────────────────────────────── */
export function useRoles() {
  const { userData } = useAuth();
  const role = normaliseRole(userData?.role);

  /** Check if current user's role rank is ≥ required rank */
  const hasRole = (required) => (RANK[role] || 0) >= (RANK[required] || 0);

  return {
    role,
    rawRole:         userData?.role || "viewer",

    /* Coarse permission gates */
    isViewer:        role === ROLES.VIEWER,
    isManager:       role === ROLES.MANAGER,
    isAdmin:         role === ROLES.ADMIN,
    isSuperAdmin:    role === ROLES.SUPERADMIN,

    /* Feature permission helpers */
    canView:         () => hasRole(ROLES.VIEWER),      // always true if logged in
    canEvaluate:     () => hasRole(ROLES.MANAGER),     // create + run decisions
    canManageUsers:  () => hasRole(ROLES.ADMIN),       // invite / change roles
    canEditFeatures: () => hasRole(ROLES.ADMIN),       // feature flags
    canChangePlans:  () => hasRole(ROLES.ADMIN),       // plan management
    canAccessAdmin:  () => hasRole(ROLES.ADMIN),       // /admin route
    canDoSystem:     () => hasRole(ROLES.SUPERADMIN),  // tenant management

    hasRole,

    /* Display helpers */
    getRoleLabelKey: () => ROLE_META[role]?.labelKey || "role_viewer",
    getRoleBadgeCls: () => ROLE_META[role]?.cls || ROLE_META[ROLES.VIEWER].cls,

    ROLES,
    ROLE_META,
  };
}
